// src/components/ui/CitySelect.jsx — Searchable City Select & Combobox Component
import { useState, useEffect, useRef } from 'react';
import { citiesApi } from '@/lib/api';
import { Search, MapPin, Check, ChevronDown, Plus, Plane, Building } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function CitySelect({
  value = '',
  onChange,
  placeholder = 'Search origin or destination city...',
  label = '',
  required = false,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch cities list with high limit
  useEffect(() => {
    let active = true;
    setLoading(true);

    citiesApi.list({ limit: 500, search: search.trim() })
      .then(({ data }) => {
        if (active) setCities(data || []);
      })
      .catch(() => {
        if (active) setCities([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [search]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cityObj) => {
    const cityName = typeof cityObj === 'string' ? cityObj : cityObj.name;
    onChange(cityName, typeof cityObj === 'object' ? cityObj : null);
    setIsOpen(false);
    setSearch('');
  };

  const selectedCityObj = cities.find(c => c.name.toLowerCase() === (value || '').toLowerCase());

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
          {label} {required && <span className="text-rose-500">*</span>}
        </label>
      )}

      {/* Select Trigger Box */}
      <div
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) setTimeout(() => inputRef.current?.focus(), 50);
        }}
        className={cn(
          "w-full h-10 px-3.5 py-2 rounded-lg border bg-white flex items-center justify-between cursor-pointer transition-all shadow-xs",
          isOpen ? "border-blue-500 ring-2 ring-blue-500/20" : "border-slate-300 hover:border-slate-400"
        )}
      >
        <div className="flex items-center gap-2 overflow-hidden min-w-0">
          <MapPin className={cn("w-4 h-4 shrink-0", value ? "text-blue-600" : "text-slate-400")} />
          {value ? (
            <span className="text-sm font-semibold text-slate-900 truncate">
              {value}
              {selectedCityObj?.airport_code && (
                <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-mono font-bold bg-blue-50 text-blue-700 rounded border border-blue-200">
                  {selectedCityObj.airport_code}
                </span>
              )}
            </span>
          ) : (
            <span className="text-sm text-slate-400 truncate">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={cn("w-4 h-4 text-slate-400 shrink-0 transition-transform", isOpen && "rotate-180")} />
      </div>

      {/* Hidden input for HTML form validation */}
      {required && (
        <input
          type="text"
          value={value}
          onChange={() => {}}
          required
          tabIndex={-1}
          className="sr-only"
        />
      )}

      {/* Search Popover Dropdown */}
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 mt-1.5 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-100 min-w-[280px]">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400 shrink-0 ml-1" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search city name, state or airport code..."
              className="w-full text-sm bg-transparent outline-none text-slate-800 placeholder:text-slate-400 py-1"
            />
          </div>

          {/* City Options List */}
          <div className="max-h-60 overflow-y-auto p-1.5 space-y-0.5">
            {loading ? (
              <div className="py-6 text-center text-xs text-slate-400 flex justify-center items-center gap-2">
                <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                Searching cities...
              </div>
            ) : cities.length > 0 ? (
              cities.map((c) => {
                const isSelected = (value || '').toLowerCase() === c.name.toLowerCase();
                return (
                  <div
                    key={c.id}
                    onClick={() => handleSelect(c)}
                    className={cn(
                      "px-3 py-2 rounded-lg flex items-center justify-between cursor-pointer text-sm font-medium transition-colors",
                      isSelected ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-slate-100/80 text-slate-700"
                    )}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                        {c.tier === 'international' ? (
                          <Plane className="w-3.5 h-3.5 text-indigo-600" />
                        ) : (
                          <Building className="w-3.5 h-3.5 text-slate-500" />
                        )}
                      </div>
                      <div className="truncate">
                        <span className="block text-slate-900 font-semibold leading-tight truncate">
                          {c.name}
                        </span>
                        <span className="block text-[11px] text-slate-400 font-normal truncate">
                          {c.state ? `${c.state}, ` : ''}{c.country}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      {c.airport_code && (
                        <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                          {c.airport_code}
                        </span>
                      )}
                      {c.tier && (
                        <span className={cn(
                          "text-[9px] uppercase font-bold px-1.5 py-0.5 rounded tracking-wider",
                          c.tier === 'tier1' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          c.tier === 'tier2' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                          c.tier === 'international' ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                          'bg-slate-100 text-slate-600 border border-slate-200'
                        )}>
                          {c.tier}
                        </span>
                      )}
                      {isSelected && <Check className="w-4 h-4 text-blue-600 ml-1" />}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-3 text-center">
                <p className="text-xs text-slate-500 mb-2">No matching cities found in database</p>
                {search.trim() && (
                  <button
                    type="button"
                    onClick={() => handleSelect(search.trim())}
                    className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" /> Use "{search.trim()}"
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
